import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Github, Loader2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";

const toRepo = (s) => (s || "app").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "app";

export const GithubDialog = ({ open, onOpenChange, project, onChange }) => {
  const [status, setStatus] = useState(null);
  const [repo, setRepo] = useState("");
  const [priv, setPriv] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.githubStatus().then(setStatus).catch(() => setStatus({ connected: false }));
    setRepo(project.github?.repo?.split("/")[1] || toRepo(project.name));
  }, [open, project.github, project.name]);

  const push = async () => {
    setBusy(true);
    try {
      const r = await api.githubPush(project.id, repo, priv);
      toast.success(r.created ? `Created ${r.repo} and pushed version ${r.version_number}` : `Pushed version ${r.version_number} to ${r.repo}`);
      onChange({ ...project, github: r });
    } catch (e) { toast.error(errMsg(e, "Push failed")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] bg-white border-slate-200" data-testid="github-dialog">
        <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2"><Github size={18} /> Push to GitHub</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">Commit the current version of your app to a repository on your GitHub account.</DialogDescription>
        {status === null ? (
          <Loader2 className="animate-spin text-slate-500 mx-auto" />
        ) : !status.connected ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-5 text-center space-y-3" data-testid="github-not-connected">
            <p className="text-sm text-slate-600">Connect your GitHub account first.</p>
            <Link to="/settings" className="inline-flex h-9 px-4 items-center rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700" data-testid="github-go-settings">Open settings</Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-slate-500">Connected as <span className="text-slate-900 font-mono">@{status.login}</span></div>
            <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 overflow-hidden focus-within:border-sky-300">
              <span className="pl-3 text-sm font-mono text-slate-500">{status.login}/</span>
              <input value={repo} onChange={(e) => setRepo(toRepo(e.target.value))} data-testid="github-repo-input" className="flex-1 h-10 bg-transparent pr-3 text-sm font-mono text-slate-900 outline-none" />
            </div>
            <label className="flex items-center justify-between text-sm text-slate-600">
              Private repository (only for new repos)
              <Switch checked={priv} onCheckedChange={setPriv} data-testid="github-private-switch" />
            </label>
            {project.github && (
              <a href={project.github.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-emerald-600 hover:underline" data-testid="github-last-push">
                Last push: {project.github.repo} · v{project.github.version_number} <ExternalLink size={11} />
              </a>
            )}
            <button onClick={push} disabled={busy || !repo} data-testid="github-push-btn" className="w-full h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 size={15} className="animate-spin" />} Push current version
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GithubDialog;
